import { takeEvery } from 'redux-saga';
import { fork, take, select } from 'redux-saga/effects';
import { getAuthedUser, getBooking, getNewBooking } from '../selectors';

import { isHomescreenLaunch } from '../util';

import {
    CREATE_BOOKING,
    CANCEL_BOOKING,
    RECEIVED_AUTHED_USER,
    LOGOUT,
    UPDATE_BOOKING_TIMES,
    CHECK_IN_BOOKING,
    CHECK_OUT_BOOKING,
    BOOKING_UPDATED,
    SET_ERROR
} from '../constants';

import {
    CREATE_BOOKING_CLICKED,
    CREATE_BOOKING_CANCELLED,
    TRACK_EDIT_BOOKING_TIME_CLICKED,
    CREATE_BOOKING_STEPPER_STEP_CHANGED,
} from '../constants/analytics';
import {
    HAMBURGER_MENU,
    TIME_LINK,
} from '../constants/editTimeReferrer';

function* trackLogin() {
    yield take(RECEIVED_AUTHED_USER);
    const user = yield select(getAuthedUser);

    // Associate the logged in user with their analytics
    analytics.identify(user.userId, {
        name: user.userName,
        email: user.emailAddress
    });

    analytics.track(`User logged in`, {
        userId: user.userId,
        name: user.userName,
        email: user.emailAddress,
        fromHomescreen: isHomescreenLaunch(),
    });
}

function* trackLogout() {
    yield take(LOGOUT);
    const analyticsUser = analytics.user();

    // Log a logout event before clearing the currently identified user's details
    analytics.track(`User logged out`, {
        userId: analyticsUser.id(),
        name: analyticsUser.traits().name,
        email: analyticsUser.traits().email
    });
    analytics.reset();
}

function* trackCancelBooking() {
    yield takeEvery(`CANCEL_BOOKING`, function*(data) {
        const booking = yield select(getBooking, data.staticUId);

        analytics.track(`Booking cancelled`, {
            booking: JSON.stringify(booking),
        });
    });
}

function* trackUpdateBookingTime() {
    yield takeEvery(UPDATE_BOOKING_TIMES, function*(data) {
        const booking = yield select(getBooking, data.staticUId);
        const eventDescription =
            data.startTime != null
                ? data.endTime != null
                    ? `Booking start and end time updated`
                    : `Booking start time updated`
                : `Booking end time updated`;

        let analyticsInformation = {
            booking: JSON.stringify(booking),
        };

        if (data.startTime != null) {
            analyticsInformation[`originalStartTime`] = booking.startTime.toISOString();
            analyticsInformation[`updatedStartTime`] = data.startTime.toISOString();
        }

        if (data.endTime != null) {
            analyticsInformation[`originalEndTime`] = booking.endTime.toISOString();
            analyticsInformation[`updatedEndTime`] = data.endTime.toISOString();
        }

        analytics.track(eventDescription, analyticsInformation);
    });
}

function* trackCreateBookingClick() {
    yield takeEvery(CREATE_BOOKING_CLICKED, function() {
        analytics.track(`Create booking clicked`);
    });
}

function* trackCreateBooking() {
    yield takeEvery(CREATE_BOOKING, function*() {
        const booking = yield select(getNewBooking);

        // driverId is not set at this point because it gets set by an API call and is currently hardcoded
        // to the logged in user by this call as we don't support book on behalf yet
        analytics.track(`New booking created`, {
            booking: JSON.stringify(booking),
        });
    });
}

function* trackCreateBookingCancel() {
    yield takeEvery(CREATE_BOOKING_CANCELLED, function*(data) {
        const booking = yield select(getNewBooking);

        analytics.track(`Cancelled creating a new booking part way through`, {
            cancelledStepIndex: data.step,
            cancelledBooking: JSON.stringify(booking),
        });
    });
}

function* trackCheckInBooking() {
    yield takeEvery(CHECK_IN_BOOKING, function*(data) {
        const booking = yield select(getBooking, data.staticUId);

        analytics.track(`Booking checked in`, {
            booking: JSON.stringify(booking),
        });
    });
}

function* trackCheckOutBooking() {
    yield takeEvery(CHECK_OUT_BOOKING, function*(data) {
        const booking = yield select(getBooking, data.staticUId);

        analytics.track(`Booking checked out`, {
            booking: JSON.stringify(booking),
        });
    });
}

function* trackEditBookingTimeClick() {
    yield takeEvery(TRACK_EDIT_BOOKING_TIME_CLICKED, function*(data) {
        const booking = yield select(getBooking, data.staticUId);
        const location = data.caller === HAMBURGER_MENU ? `in hamburger clicked` : `link clicked`;

        analytics.track(`Edit booking time ${location}`, {
            booking: JSON.stringify(booking),
        });
    });
}

function* trackErrorShown() {
    yield takeEvery(SET_ERROR, function*(data) {
        analytics.track(`Error shown to user`, {
            error: data.messageLines,
            options: data.options,
        });
    });
}

function* trackCreateBookingStepChanged() {
    yield takeEvery(CREATE_BOOKING_STEPPER_STEP_CHANGED, function*(data) {
        analytics.track(`Changed step in the Create Booking flow`, {
            currentStep: data.currentStep,
        });
    });
}

export default function* analyticsSaga() {
    yield [
        fork(trackLogin),
        fork(trackLogout),
        fork(trackCancelBooking),
        fork(trackUpdateBookingTime),
        fork(trackCreateBooking),
        fork(trackCreateBookingClick),
        fork(trackCreateBookingCancel),
        fork(trackCheckInBooking),
        fork(trackCheckOutBooking),
        fork(trackEditBookingTimeClick),
        fork(trackErrorShown),
        fork(trackCreateBookingStepChanged),
    ];
}



// WEBPACK FOOTER //
// ./scripts/sagas/analytics.js